import { StudioSystemType } from 'types/global'
import { ProjectType } from 'types/projects'
import { ExistingGenEstimateDetails } from 'types/studio/items'
import { SystemDataType } from 'types/systems'
import { getExistingSystem } from './useExistingSystem'

/** PVWatts requires dc_ac_ratio > 0; block zero in simple retrofit UI. */
export const MIN_DC_TO_AC_RATIO = 0.01

/** Global fallback when country has no specific default. */
export const DEFAULT_DC_TO_AC_RATIO = 1.33
export const DEFAULT_DC_TO_AC_RATIO_GB = 1.5

const DEFAULT_DC_AC_RATIO_BY_COUNTRY: Record<string, number> = {
  AU: DEFAULT_DC_TO_AC_RATIO,
  GB: DEFAULT_DC_TO_AC_RATIO_GB,
}

export const getDefaultDcToAcRatio = (countryIso2?: string): number =>
  (countryIso2 ? DEFAULT_DC_AC_RATIO_BY_COUNTRY[countryIso2] : undefined) ?? DEFAULT_DC_TO_AC_RATIO

// Source of truth to determine whether a system is retrofit system
export const isExistingSystem = <T extends SystemDataType | StudioSystemType>(system: T): boolean => {
  return !!system.is_current
}

export const isSimpleEstimateExistingSystem = (system: StudioSystemType): boolean => {
  return isExistingSystem(system) && system.is_simple_generation_estimate
}

export const isSoldSystem = (system: StudioSystemType, project: ProjectType): boolean => {
  const { system_sold: soldSystemUrl, systems } = project
  if (!soldSystemUrl || !systems?.length) return false
  const soldSystem = systems.find(({ url }) => url === soldSystemUrl)
  return soldSystem?.uuid === system.uuid
}

export const DEFAULT_PANEL_SIZE = 300

const getDefaultAdvancedSettings = (countryIso2: string | undefined) => {
  return {
    installation_year: new Date().getFullYear() - 5,
    dc_to_ac_ratio: getDefaultDcToAcRatio(countryIso2),
    panel_groups: [
      {
        group_slope_degrees: 20,
        group_azimuth: 0,
        panel_rated_power_watts: DEFAULT_PANEL_SIZE,
      },
    ],
  }
}

export const getDefaultExistingGenerationEstimateDetails = (countryIso2?: string): ExistingGenEstimateDetails => {
  const defaultAdvanceSetting = getDefaultAdvancedSettings(countryIso2)
  return {
    estimation_method: 'number_panels_and_wattage',
    panels_painted_in_studio: true,
    panel_rated_power_watts: DEFAULT_PANEL_SIZE,
    ...defaultAdvanceSetting,
  }
}

export const removeExistingSystem = () => {
  const existingSystem = getExistingSystem(window.editor?.getSystems())
  if (existingSystem) {
    window.editor.execute(new RemoveObjectCommand(existingSystem, true))
  }
}

export const createExistingSystem = ({ countryIso2 }: { countryIso2: string }) => {
  const performanceCalculator = window.WorkspaceHelper.getDefaultPerformanceCalculator()
  const newSystem = new OsSystem({
    calculator: performanceCalculator,
    show_customer: false,
    is_current: true,
    is_simple_generation_estimate: true,
    existing_generation_estimate_details: getDefaultExistingGenerationEstimateDetails(countryIso2),
  })
  window.editor.execute(new AddObjectCommand(newSystem, undefined, true))
}
